import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { getCurrentUser, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { AdminUser } from '../types';
import { logDebug } from '../utils/logger';

interface AuthContextType {
  user: AdminUser | null;
  userId: string | null;
  isLoading: boolean;
  isAdmin: boolean;
  isMember: boolean;
  // Admin MFA enforcement: derived from the AdminsVerified / AdminsSetupRequired
  // Cognito groups maintained by the postAuthMfaEnforcer trigger.
  isAdminsVerified: boolean;
  isAdminSetupRequired: boolean;
  groups: string[];
  signOut: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [isAdminsVerified, setIsAdminsVerified] = useState(false);
  const [isAdminSetupRequired, setIsAdminSetupRequired] = useState(false);
  const [groups, setGroups] = useState<string[]>([]);

  // Inactivity timeout: 6 hours for admins, 24 hours for members
  // (setTimeout clamps values above ~24.8 days to 1ms, which would log members out instantly)
  const isAdminUser = groups.includes('Admins');
  const INACTIVITY_TIMEOUT = isAdminUser ? 6 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Only set timer if user is authenticated
    if (user) {
      inactivityTimerRef.current = setTimeout(async () => {
        logDebug('AuthContext', `User inactive for ${isAdminUser ? '6 hours' : '24 hours'}, logging out...`);
        await handleSignOut();
      }, INACTIVITY_TIMEOUT);
    }
  }, [user, isAdminUser]);

  const checkAuthState = async () => {
    try {
      setIsLoading(true);
      const currentUser = await getCurrentUser();
      let session = await fetchAuthSession();
      let groups = (session.tokens?.accessToken?.payload['cognito:groups'] as string[]) || [];

      // Admin MFA off-by-one fix: the postAuthMfaEnforcer PostAuthentication trigger
      // adds the user to AdminsVerified DURING the MFA sign-in, but the access token
      // minted for that same sign-in predates the group add. Without this, a freshly
      // enrolled admin is stuck on /admin/setup-mfa until they sign in a second time.
      // If we see an admin who is not yet AdminsVerified, force one token refresh so
      // the just-added group is reflected. Harmless for genuine setup-required admins
      // (they simply stay out of AdminsVerified and get the setup page as intended).
      if (groups.includes('Admins') && !groups.includes('AdminsVerified')) {
        try {
          session = await fetchAuthSession({ forceRefresh: true });
          groups = (session.tokens?.accessToken?.payload['cognito:groups'] as string[]) || [];
        } catch {
          // Keep the non-refreshed groups; the setup-page redirect still applies.
        }
      }

      // Extract user information
      const userInfo: AdminUser = {
        username: currentUser.username,
        email: currentUser.signInDetails?.loginId || '',
        groups,
      };

      // Check if user has admin privileges
      const hasAdminAccess = groups.includes('Admins');

      setUser(userInfo);
      setUserId(currentUser.userId);
      setIsAdmin(hasAdminAccess);
      setIsMember(groups.includes('Members') || groups.includes('Admins'));
      setIsAdminsVerified(groups.includes('AdminsVerified'));
      setIsAdminSetupRequired(groups.includes('AdminsSetupRequired'));
      setGroups(groups);
    } catch {
      // User not authenticated — expected for unauthenticated visitors
      setUser(null);
      setUserId(null);
      setIsAdmin(false);
      setIsMember(false);
      setIsAdminsVerified(false);
      setIsAdminSetupRequired(false);
      setGroups([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      // Clear inactivity timer on logout
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }

      await signOut();
      setUser(null);
      setUserId(null);
      setIsAdmin(false);
      setIsMember(false);
      setIsAdminsVerified(false);
      setIsAdminSetupRequired(false);
      setGroups([]);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const refreshAuth = async () => {
    await checkAuthState();
  };

  useEffect(() => {
    checkAuthState();
  }, []); // Run once on mount

  useEffect(() => {
    // Activity event listeners to reset inactivity timer
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      resetInactivityTimer();
    };

    // Add event listeners for user activity
    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    // Start timer if user is authenticated
    resetInactivityTimer();

    // Cleanup function
    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [user, resetInactivityTimer]);

  const value: AuthContextType = {
    user,
    userId,
    isLoading,
    isAdmin,
    isMember,
    isAdminsVerified,
    isAdminSetupRequired,
    groups,
    signOut: handleSignOut,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
